// src/components/common/Checkbox/Checkbox.tsx
"use client";

import React from "react";
import { useField, useFormikContext } from "formik";
import FieldError from "../field-error/FieldError";
import "./Checkbox.css";

interface CheckboxProps {
  // Formik mode
  name?: string;

  // UI
  label: React.ReactNode;
  disabled?: boolean;

  // Controlled (non-Formik) mode
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Legacy escape hatch
  field?: any;
}

const Checkbox: React.FC<CheckboxProps> = ({
  name,
  label,
  disabled,
  checked,
  onChange,
  field: customField,
}) => {
  // --------------------------------------------------
  // Detect Formik context safely
  // --------------------------------------------------
  const formik = useFormikContext<any | null>();
  const hasFormik = !!formik && !!name && !customField;

  // --------------------------------------------------
  // Controlled (non-Formik) mode
  // --------------------------------------------------
  if (!hasFormik && !customField) {
    const checkboxId = `checkbox-${Math.random().toString(36).slice(2)}`;

    return (
      <div className={`checkbox-group ${disabled ? "checkbox-disabled" : ""}`}>
        <label htmlFor={checkboxId} className="checkbox-label">
          <input
            id={checkboxId}
            type="checkbox"
            checked={!!checked}
            onChange={onChange}
            disabled={disabled}
            className="checkbox-input"
          />
          <span className="checkbox-text">{label}</span>
        </label>
      </div>
    );
  }

  // --------------------------------------------------
  // Formik or legacy field mode
  // --------------------------------------------------
  const [field, meta] = hasFormik
    ? useField({ name: name!, type: "checkbox" })
    : [null, null];

  const mergedField = customField || field;
  const checkboxId = `${mergedField.name}-checkbox`;
  const errorText = meta?.touched && meta?.error ? meta.error : undefined;

  return (
    <div className={`checkbox-group ${disabled ? "checkbox-disabled" : ""}`}>
      <label htmlFor={checkboxId} className="checkbox-label">
        <input
          id={checkboxId}
          type="checkbox"
          {...mergedField}
          disabled={disabled}
          className="checkbox-input"
          aria-invalid={!!errorText}
          aria-describedby={errorText ? `${checkboxId}-error` : undefined}
        />
        <span className="checkbox-text">{label}</span>
      </label>

      {errorText && (
        <div id={`${checkboxId}-error`}>
          <FieldError>{errorText}</FieldError>
        </div>
      )}
    </div>
  );
};

export default Checkbox;